import { useEffect, useRef, useState } from "react";
import { SecretField } from "@leylion/design-system";
import {
  getStoredContext,
  hasSessionContextChanged,
  SESSION_CONTEXT_CHANGED_EVENT
} from "../api/client";
import { validateNewPin } from "../security/pin-policy";
import {
  getFirstLoginManagerPinState,
  setFirstLoginManagerPin
} from "./first-login-manager-pin-client";
import "./first-login-manager-pin.css";

type Language = "ar" | "en";

function problemCode(error: unknown): string {
  const problem = error as Error & { status?: number; problem?: { title?: string } };
  return problem.problem?.title ?? problem.message ?? "";
}

export function FirstLoginManagerPinGate() {
  const [contextKey, setContextKey] = useState("");
  const [visible, setVisible] = useState(false);
  const [sessionBlocked, setSessionBlocked] = useState(hasSessionContextChanged);
  const [checkFailed, setCheckFailed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [language, setLanguage] = useState<Language>(() =>
    localStorage.getItem("leylion.language") === "en" ? "en" : "ar"
  );
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const generationRef = useRef(0);
  const contextKeyRef = useRef("");

  useEffect(() => {
    const block = () => {
      // Invalidate pending PIN reads before their rejection handlers can render
      // onboarding over the session recovery notice.
      generationRef.current += 1;
      setSessionBlocked(true);
      setVisible(false);
      setPin("");
      setConfirmPin("");
    };
    window.addEventListener(SESSION_CONTEXT_CHANGED_EVENT, block);
    if (hasSessionContextChanged()) block();
    return () => window.removeEventListener(SESSION_CONTEXT_CHANGED_EVENT, block);
  }, []);

  useEffect(() => {
    if (sessionBlocked) return;
    const syncContext = () => {
      if (hasSessionContextChanged()) return;
      const context = getStoredContext();
      const next = context ? `${context.tenantId}:${context.branchId}` : "";
      if (contextKeyRef.current !== next) {
        contextKeyRef.current = next;
        generationRef.current += 1;
        setVisible(false);
        setCheckFailed(false);
        setPin("");
        setConfirmPin("");
        setError("");
        setContextKey(next);
      }
    };
    syncContext();
    const timer = window.setInterval(syncContext, 250);
    return () => window.clearInterval(timer);
  }, [sessionBlocked]);

  useEffect(() => {
    if (!contextKey || sessionBlocked || hasSessionContextChanged()) return;
    const generation = generationRef.current;
    let cancelled = false;
    const isCurrent = () => !cancelled && generationRef.current === generation && !hasSessionContextChanged();
    setChecking(true);
    getFirstLoginManagerPinState()
      .then((state) => {
        if (isCurrent()) {
          if (typeof state?.configured !== "boolean") throw new Error("PIN_STATE_UNAVAILABLE");
          setCheckFailed(false);
          setError("");
          setVisible(!state.configured);
        }
      })
      .catch((caught: unknown) => {
        if (!isCurrent()) return;
        const status = (caught as { status?: number }).status;
        const code = (caught as { code?: string }).code ?? problemCode(caught);
        if (status === 404 || code === "FIRST_LOGIN_NOT_AVAILABLE"
          || code === "SESSION_CONTEXT_CHANGED" || code === "SESSION_CONTEXT_STALE_RESPONSE") {
          setVisible(false);
          return;
        }
        setError(
          language === "ar"
            ? "تعذر التحقق من حالة رمز المدير. أعد المحاولة."
            : "Manager PIN status could not be checked. Try again."
        );
        // An unavailable read is not evidence that the user has no PIN.
        setCheckFailed(true);
        setVisible(true);
      })
      .finally(() => { if (isCurrent()) setChecking(false); });
    return () => { cancelled = true; };
  }, [contextKey, language, checkAttempt, sessionBlocked]);

  if (sessionBlocked || hasSessionContextChanged() || !contextKey || !visible) return null;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const pinError = validateNewPin(pin, language);
    if (pinError) {
      setError(pinError);
      return;
    }
    if (pin !== confirmPin) {
      setError(language === "ar" ? "الرمزان غير متطابقين." : "The PIN values do not match.");
      return;
    }

    try {
      setBusy(true);
      setError("");
      const result = await setFirstLoginManagerPin(pin);
      if (result.configured) {
        setPin("");
        setConfirmPin("");
        setVisible(false);
      }
    } catch (caught) {
      const code = problemCode(caught);
      setError(
        code === "PIN_POLICY_FAILED" || code === "PIN_TOO_WEAK"
          ? validateNewPin(pin, language) || (language === "ar" ? "تعذر قبول رمز PIN." : "The PIN could not be accepted.")
          : language === "ar"
            ? "تعذر حفظ رمز المدير. أعد المحاولة."
            : "The manager PIN could not be saved. Try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const isArabic = language === "ar";
  return (
    <div className="first-login-manager-pin-root" dir={isArabic ? "rtl" : "ltr"} data-testid="first-login-manager-pin">
      <div className="first-login-manager-pin-backdrop" />
      <section className="first-login-manager-pin-panel" role="dialog" aria-modal="true">
        <button
          className="first-login-manager-pin-language"
          type="button"
          onClick={() => {
            const next = isArabic ? "en" : "ar";
            localStorage.setItem("leylion.language", next);
            setLanguage(next);
          }}
        >
          {isArabic ? "EN" : "AR"}
        </button>
        <span className="first-login-manager-pin-badge">
          {isArabic ? "حماية العمليات الإدارية" : "Protect manager operations"}
        </span>
        <h2>{checkFailed
          ? (isArabic ? "تعذر التحقق من رمز المدير" : "Manager PIN status unavailable")
          : (isArabic ? "أنشئ رمز المدير" : "Create your manager PIN")}</h2>
        <p>
          {checkFailed
            ? (isArabic
              ? "لم نتمكن من قراءة حالة رمز المدير. أعد المحاولة للمتابعة."
              : "We could not read your manager PIN status. Retry to continue.")
            : isArabic
            ? "سيُستخدم هذا الرمز لتأكيد الموافقات والعمليات الحساسة. يبقى منفصلًا عن كلمة مرور حسابك، ولا يُحفظ محليًا. يمكنك إظهاره مؤقتًا أثناء الإدخال ثم إخفاؤه مجددًا."
            : "This PIN confirms approvals and sensitive actions. It stays separate from your account password and is not persisted locally. You can reveal it temporarily while entering it, then hide it again."}
        </p>
        {checkFailed ? (
          <div className="nx-stack" data-testid="first-login-manager-pin-unavailable">
            <div className="first-login-manager-pin-error" role="alert">{error}</div>
            <button type="button" className="first-login-manager-pin-submit"
              data-testid="first-login-manager-pin-retry" disabled={checking}
              onClick={() => setCheckAttempt((attempt) => attempt + 1)}>
              {checking ? "…" : isArabic ? "أعد المحاولة" : "Retry"}
            </button>
          </div>
        ) : (
        <form onSubmit={save}>
          <SecretField
            data-testid="first-login-manager-pin-value"
            label={isArabic ? "رمز المدير" : "Manager PIN"}
            inputMode="numeric"
            autoComplete="off"
            minLength={4}
            maxLength={8}
            value={pin}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 8))}
            revealLabel={isArabic ? "إظهار رمز المدير" : "Show manager PIN"}
            concealLabel={isArabic ? "إخفاء رمز المدير" : "Hide manager PIN"}
            autoFocus
          />
          <SecretField
            data-testid="first-login-manager-pin-confirm"
            label={isArabic ? "تأكيد الرمز" : "Confirm PIN"}
            inputMode="numeric"
            autoComplete="off"
            minLength={4}
            maxLength={8}
            value={confirmPin}
            onChange={(event) => setConfirmPin(event.target.value.replace(/\D/g, "").slice(0, 8))}
            revealLabel={isArabic ? "إظهار تأكيد الرمز" : "Show PIN confirmation"}
            concealLabel={isArabic ? "إخفاء تأكيد الرمز" : "Hide PIN confirmation"}
          />
          {error ? <div className="first-login-manager-pin-error" role="alert">{error}</div> : null}
          <button
            data-testid="first-login-manager-pin-submit"
            className="first-login-manager-pin-submit"
            type="submit"
            disabled={busy || Boolean(validateNewPin(pin, language)) || pin !== confirmPin}
          >
            {busy ? "…" : isArabic ? "حفظ الرمز والمتابعة" : "Save PIN and continue"}
          </button>
        </form>
        )}
      </section>
    </div>
  );
}
